// Maps live data into the design component's DW_GRID_DATA shape.
//
// The Dwellow Design System component (vendor/dwellowGrid.js) renders three views
// from a single DW_GRID_DATA object whose SHAPE is the contract. This module is
// the "wiring to live data" the designer's data.js anticipated: it turns
//   - the live Carbon Intensity API (now / mix / today / forecast), and
//   - the /grid_records and /grid_history endpoint responses
// into that shape, and generates the plain-English captions the component shows.
//
// Records values are taken verbatim from what the APIs compute, never curated
// or announced figures, so nothing on screen diverges from the source data.

import { readGrid, orderedMix, shareOf, sumShares } from './insights.js';

const CLEAN_FUELS = ['solar', 'wind', 'hydro', 'nuclear', 'biomass'];
const MIXED_FUELS = ['imports', 'other'];
const FUEL_NAME = {
    solar: 'Solar', wind: 'Wind', hydro: 'Hydro', nuclear: 'Nuclear', biomass: 'Biomass',
    imports: 'Imports', other: 'Other', gas: 'Gas', coal: 'Coal',
};
const RECORD_ICON = { wind: 'wind', solar: 'sun', zeroCarbon: 'leaf', lowestIntensity: 'sparkle', cleanestYear: 'trophy' };
const RECORD_ORDER = ['wind', 'solar', 'zeroCarbon', 'lowestIntensity', 'cleanestYear'];
const TRAFFIC_TO_BAND = { green: 'clean', amber: 'moderate', red: 'high' };

// Null/'' must stay null (Number(null) === 0 would silently zero out a missing
// "actual" value and wreck the forecast half of the day curve).
const num = v => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};
const clamp01 = v => Math.max(0, Math.min(1, v));
const groupOf = fuel => (CLEAN_FUELS.includes(fuel) ? 'clean' : MIXED_FUELS.includes(fuel) ? 'mixed' : 'fossil');

// design band from an intensity value (matches the component's own thresholds)
function bandFromIntensity(g) {
    if (!Number.isFinite(g)) return 'moderate';
    return g < 130 ? 'clean' : g < 210 ? 'moderate' : 'high';
}

function timeLabel(date) {
    try { return date.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' }).toLowerCase().replace(' ', ''); }
    catch { return date.toISOString().slice(11, 16); }
}
function dayQualifier(date, now) {
    if (date.toDateString() === now.toDateString()) {
        const h = date.getHours();
        return h < 5 ? ' tonight' : '';
    }
    const t = new Date(now.getTime()); t.setDate(now.getDate() + 1);
    if (date.toDateString() === t.toDateString()) return date.getHours() < 12 ? ' tomorrow' : ' tomorrow';
    try { return ' ' + date.toLocaleDateString('en-GB', { weekday: 'long' }); } catch { return ''; }
}
function fmtRecordDate(iso, year) {
    if (year) return String(year);
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
// A friendly clock label for a whole-hour index, matching the day chart's axis.
function hourLabel(h) {
    const hr24 = Math.round(h) % 24;
    const period = hr24 < 12 ? 'am' : 'pm';
    const hr12 = hr24 % 12 === 0 ? 12 : hr24 % 12;
    return `${hr12}${period}`;
}
function monthDayLabel(monthDay) {
    if (!monthDay) return 'today';
    const [mm, dd] = monthDay.split('-').map(Number);
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${dd} ${months[(mm || 1) - 1]}`;
}

// A hand-shaped fallback day curve (hourly g, 00:00→24:00) when today's live
// series is unavailable, low overnight & midday, an early-evening peak.
const DEFAULT_HOURLY = [110, 105, 101, 98, 96, 95, 101, 120, 150, 165, 150, 136, 129, 131, 133, 136, 152, 176, 205, 215, 201, 176, 150, 130, 116];

// ---- carbon24: 25 hourly points for the "through the day" chart -----------
function buildCarbon24(todaySeries, now) {
    const nowMs = now.getTime();
    const byHour = new Map();
    // Bucket by LOCAL hour so the axis + "cleanest around" labels match the
    // reader's clock (the API stamps periods in UTC).
    for (const p of todaySeries || []) {
        const from = new Date(p.from);
        if (Number.isNaN(from.getTime()) || from.getMinutes() !== 0) continue;
        const g = num(p?.intensity?.actual) ?? num(p?.intensity?.forecast);
        if (g == null) continue;
        byHour.set(from.getHours(), { g, startMs: from.getTime() });
    }
    const nowHour = Math.min(23.99, now.getHours() + now.getMinutes() / 60);
    if (byHour.size < 6) {
        // No real day series: use an illustrative curve and flag it, so callers
        // don't present fabricated clock times as today's actual cleanest/highest.
        return {
            carbon24: DEFAULT_HOURLY.map((g, h) => ({ h, g, forecast: h > nowHour })),
            nowHour,
            synthetic: true,
        };
    }
    const carbon24 = [];
    let lastG = byHour.get(0)?.g ?? 150;
    for (let h = 0; h <= 24; h++) {
        const rec = byHour.get(h) || byHour.get(Math.min(23, h));
        const g = rec ? rec.g : lastG;
        lastG = g;
        const startMs = rec ? rec.startMs : nowMs + 1;
        carbon24.push({ h, g, forecast: startMs > nowMs });
    }
    return { carbon24, nowHour, synthetic: false };
}

// ---- greenWindow: the greenest contiguous stretch ahead -------------------
function buildGreenWindow(forecastSeries, now) {
    const future = (forecastSeries || [])
        .map(e => ({ start: new Date(e.from), g: num(e?.intensity?.forecast) }))
        .filter(e => !Number.isNaN(e.start.getTime()) && e.g != null && e.start.getTime() > now.getTime());
    if (future.length === 0) {
        return { fromLabel: 'overnight', toLabel: 'the early hours', verdict: 'usually the cleanest stretch', action: 'The small hours are typically the greenest time to run appliances.' };
    }
    let minIdx = 0;
    future.forEach((e, i) => { if (e.g < future[minIdx].g) minIdx = i; });
    const threshold = future[minIdx].g * 1.12 + 6;
    let lo = minIdx, hi = minIdx;
    while (lo > 0 && future[lo - 1].g <= threshold) lo--;
    while (hi < future.length - 1 && future[hi + 1].g <= threshold) hi++;
    const start = future[lo].start;
    const end = new Date(future[hi].start.getTime() + 30 * 60 * 1000);
    const band = bandFromIntensity(future[minIdx].g);
    return {
        fromLabel: `${timeLabel(start)}${dayQualifier(start, now)}`,
        toLabel: timeLabel(end),
        verdict: band === 'clean' ? 'the cleanest stretch in the next day' : 'the greenest window coming up',
        action: 'A good time to run the washing machine or charge the car.',
    };
}

// The actionable second line. It never names a specific time or weather — it
// points at the forecast strip below (which shows the real greenest window), so
// it can't contradict the forecast.
function sublineFor(traffic) {
    if (traffic === 'green') {
        return 'A great moment to put a wash on or charge the car, this is about as clean as UK electricity gets.';
    }
    if (traffic === 'red') {
        return 'If a big load can wait, hold it for the greenest window below.';
    }
    return 'Fine to run the big appliances now, or hold the big jobs for the greenest window below.';
}

// ---- NOW ------------------------------------------------------------------
export function buildNow({ intensity, mix, todaySeries, forecastSeries, now = new Date() }) {
    const read = readGrid({ intensity, mix, localHour: now.getHours(), isWeekend: [0, 6].includes(now.getDay()) });
    const cleanShare = sumShares(mix, CLEAN_FUELS);
    const actual = num(intensity?.actual) ?? num(intensity?.forecast);

    const designMix = orderedMix(mix)
        .map(seg => ({ id: seg.fuel, name: FUEL_NAME[seg.fuel] || seg.fuel, pct: Math.round(seg.perc), group: groupOf(seg.fuel), _eng: `${seg.perc.toFixed(1)}%` }))
        // clean group first (so the size of the clean region tells the story), then mixed, then fossil
        .sort((a, b) => {
            const rank = { clean: 0, mixed: 1, fossil: 2 };
            return rank[a.group] - rank[b.group] || b.pct - a.pct;
        });

    const overallLeader = designMix.slice().sort((a, b) => b.pct - a.pct)[0];
    const cleanLeader = designMix.filter(f => f.group === 'clean').sort((a, b) => b.pct - a.pct)[0];
    const { carbon24, nowHour, synthetic } = buildCarbon24(todaySeries, now);

    // Cleanest / highest points of the day curve, for the chart's footnote — only
    // when we have a real day series (never label the illustrative fallback curve).
    const dayLow = carbon24.reduce((a, b) => (b.g < a.g ? b : a), carbon24[0]);
    const dayHigh = carbon24.reduce((a, b) => (b.g > a.g ? b : a), carbon24[0]);

    const mixCaption = cleanShare >= 50
        ? `More than <b>half is clean</b> right now, ${cleanLeader ? cleanLeader.name.toLowerCase() : 'wind and sun'} leading the way.`
        : `<b>${Math.round(cleanShare)}% is clean</b> right now${overallLeader ? `, ${overallLeader.name.toLowerCase()} the single biggest source` : ''}.`;

    return {
        updatedLabel: `Updated ${timeLabel(now)}`,
        band: TRAFFIC_TO_BAND[read.traffic] || 'moderate',
        statusWord: read.traffic === 'green' ? 'Clean' : read.traffic === 'red' ? 'High' : 'Moderate',
        headline: read.headline,
        subline: sublineFor(read.traffic),
        gaugeG: actual,
        _engIntensity: actual != null ? `${actual} gCO₂/kWh` : 'n/a',
        mixCaption,
        mix: designMix,
        carbon24,
        nowHour,
        dayLowLabel: synthetic ? null : hourLabel(dayLow.h),
        dayHighLabel: synthetic ? null : hourLabel(dayHigh.h),
        greenWindow: buildGreenWindow(forecastSeries, now),
    };
}

// ---- TRENDS ---------------------------------------------------------------
export function buildTrends(history, now = new Date()) {
    const yearly = Array.isArray(history?.yearly) ? history.yearly.filter(y => Number.isFinite(y.year)) : [];
    const greening = yearly.map(y => ({ y: y.year, clean: Math.round(num(y.zeroCarbonPct) || 0), fossil: Math.round(num(y.fossilPct) || 0) }));
    const yearCarbon = yearly.map(y => ({ y: y.year, g: Math.round(num(y.carbonIntensity) || 0) }));

    const settled = yearly.filter(y => Number.isFinite(y.carbonIntensity) && y.year < now.getUTCFullYear());
    const cleanest = settled.reduce((best, y) => (!best || y.carbonIntensity < best.carbonIntensity ? y : best), null) || yearly[yearly.length - 1] || { year: 2024, carbonIntensity: 124 };
    const first = yearly[0] || { year: 2009, carbonIntensity: 445 };
    const last = yearly[yearly.length - 1] || first;
    const ratio = first.carbonIntensity && cleanest.carbonIntensity ? (first.carbonIntensity / cleanest.carbonIntensity) : null;

    const otd = buildOnThisDay(history, now);

    return {
        greening,
        yearCarbon,
        cleanestYear: { y: cleanest.year, _eng: `${Math.round(cleanest.carbonIntensity)} gCO₂/kWh` },
        greeningCaption: `In ${first.year}, only about ${greening[0] ? greening[0].clean : 25}% of Britain’s power was clean. Today it’s about ${greening.length ? greening[greening.length - 1].clean : 70}%, the rest mostly gas and imports.`,
        yearCaption: `Britain’s grid has got far cleaner over the years, <b>${cleanest.year} was the cleanest yet</b>${ratio ? `, about ${ratio.toFixed(1)}× cleaner than ${first.year}` : ''}.`,
        numsBody: `Cleanest year ${cleanest.year} <span>${Math.round(cleanest.carbonIntensity)} gCO₂/kWh</span> · ${first.year} ≈ <span>${Math.round(first.carbonIntensity)} gCO₂/kWh</span> · clean share ${last.year} ≈ <span>${Math.round(last.zeroCarbonPct || greening[greening.length - 1]?.clean || 0)}%</span>`,
        onThisDay: otd,
    };
}

function otdMix(row) {
    // row.mix is [{id,name,pct}] from the endpoint; keep clean-first, drop zeros.
    const mix = Array.isArray(row?.mix) ? row.mix : [];
    return mix
        .filter(f => Number(f.pct) > 0)
        .map(f => ({ id: f.id, name: f.name || FUEL_NAME[f.id] || f.id, pct: Math.round(Number(f.pct)) }))
        .sort((a, b) => {
            const rank = { clean: 0, mixed: 1, fossil: 2 };
            return rank[groupOf(a.id)] - rank[groupOf(b.id)] || b.pct - a.pct;
        });
}

function otdShare(row, id) {
    const f = (row.mix || []).find(m => m.id === id);
    return f ? Math.round(Number(f.pct)) : 0;
}
// Purely factual: states the actual gas/wind/solar shares then vs now (or the
// coal story), then the carbon comparison. No weather, no causation — nothing the
// mix data doesn't directly show.
function otdVerdict(thenRow, nowRow) {
    const s = (r, id) => otdShare(r, id);
    const thenCoal = s(thenRow, 'coal'), nowCoal = s(nowRow, 'coal');
    const thenCI = Math.round(num(thenRow.carbonIntensity) || 0);
    const nowCI = Math.round(num(nowRow.carbonIntensity) || 0);

    let lead;
    if (thenCoal >= 6 && nowCoal <= 1) {
        lead = `On this day in ${thenRow.year}, coal made about <b>${thenCoal}%</b> of Britain’s power. This year there’s ${nowCoal === 0 ? 'none at all' : 'almost none'}`;
    } else {
        lead = `On this day in ${thenRow.year}, gas made <b>${s(thenRow, 'gas')}%</b>, wind <b>${s(thenRow, 'wind')}%</b> and solar <b>${s(thenRow, 'solar')}%</b>; this year it’s gas ${s(nowRow, 'gas')}%, wind ${s(nowRow, 'wind')}% and solar ${s(nowRow, 'solar')}%`;
    }

    let tail = '.';
    if (nowCI && thenCI) {
        if (nowCI <= thenCI * 0.95) tail = `, and it’s running cleaner this year than it did then.`;
        else if (nowCI >= thenCI * 1.05) tail = `, and it’s running a little dirtier this year than it did then.`;
        else tail = `, and it’s running about as clean as it did back then.`;
    }
    return lead + tail;
}

function buildOnThisDay(history, now) {
    const rows = (Array.isArray(history?.onThisDay) ? history.onThisDay : []).filter(r => Array.isArray(r.mix) && r.mix.length);
    const dateLabel = monthDayLabel(history?.monthDay);
    if (rows.length < 2) {
        return { dateLabel, then: { year: 'n/a', mix: [], band: 'high' }, now: { year: now.getUTCFullYear(), mix: [], band: 'moderate' }, verdict: '', thenOptions: [], thenIndex: 0 };
    }
    const nowRow = rows[rows.length - 1];
    const nowView = { year: nowRow.year, mix: otdMix(nowRow), band: bandFromIntensity(num(nowRow.carbonIntensity)) };

    // Every earlier year is a selectable comparison; the reader can cycle through them.
    const thenOptions = rows.slice(0, -1).map(r => ({
        year: r.year,
        mix: otdMix(r),
        band: bandFromIntensity(num(r.carbonIntensity)),
        verdict: otdVerdict(r, nowRow),
    }));
    // default to the comparison closest to a decade earlier
    const target = nowRow.year - 10;
    let thenIndex = 0;
    thenOptions.forEach((o, i) => {
        if (Math.abs(o.year - target) < Math.abs(thenOptions[thenIndex].year - target)) thenIndex = i;
    });

    return {
        dateLabel,
        now: nowView,
        thenOptions,
        thenIndex,
        then: thenOptions[thenIndex],
        verdict: thenOptions[thenIndex].verdict,
    };
}

// ---- RECORDS --------------------------------------------------------------
export function buildRecords(records, { liveNow, currentYearG } = {}) {
    const anchor = records?.coalFreeSince || '2024-09-30T23:00:00Z';
    const anchorDate = new Date(anchor);
    const days = Number.isNaN(anchorDate.getTime()) ? 0 : Math.max(0, Math.floor((Date.now() - anchorDate.getTime()) / 86400000));
    const byKey = new Map((records?.cards || []).map(c => [c.key, c]));

    const list = RECORD_ORDER
        .map(key => byKey.get(key))
        .filter(Boolean)
        .map(card => {
            const value = num(card.value);
            const { nowFrac, offCopy } = liveFraction(card.key, value, { liveNow, currentYearG, recentPct: card.recentPct });
            return {
                id: card.key,
                icon: RECORD_ICON[card.key] || 'sparkle',
                title: card.label,
                dateLabel: fmtRecordDate(card.at, card.year),
                _eng: `${value}${card.unit ? ' ' + card.unit : ''}`,
                nowFrac,
                offCopy,
                caption: card.blurb || '',
            };
        });

    return {
        coal: { days, sinceLabel: 'since Britain last burned coal for power', anchorISO: anchor.slice(0, 10) },
        list,
    };
}

function liveFraction(key, record, { liveNow = {}, currentYearG, recentPct }) {
    const roundPct = f => Math.round(clamp01(f) * 100);
    switch (key) {
        case 'wind':
        case 'solar': {
            const frac = clamp01((num(recentPct) || 0) / 100);
            const off = 100 - roundPct(frac);
            // Output-relative only — no weather claims (a 0% share can just be night).
            const copy = frac >= 0.75 ? `only ${off}% off` : `${roundPct(frac)}% of the record`;
            return { nowFrac: frac, offCopy: copy };
        }
        case 'zeroCarbon': {
            const frac = clamp01((num(liveNow.zeroCarbonShare) || 0) / (record || 100));
            return { nowFrac: frac, offCopy: frac >= 0.5 ? 'over halfway there' : `${roundPct(frac)}% of the way` };
        }
        case 'lowestIntensity': {
            const actual = num(liveNow.actual);
            const frac = actual ? clamp01(record / actual) : 0.3;
            const mult = actual && record ? Math.max(1, Math.round(actual / record)) : null;
            return { nowFrac: frac, offCopy: mult ? `~${mult}× cleaner than now` : 'no live reading' };
        }
        case 'cleanestYear': {
            const frac = currentYearG && record ? clamp01(record / currentYearG) : 0.9;
            return { nowFrac: frac, offCopy: frac >= 0.9 ? 'this year is tracking close' : 'a little behind so far' };
        }
        default:
            return { nowFrac: 0.5, offCopy: '' };
    }
}
